import React, { useContext, useState } from 'react';
import { Menu, Sun, Moon, Maximize, Minimize, User, LogOut, Store } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import { Dropdown, DropdownItem } from '../common/Dropdown';
import { getShopNameByCode } from '../../config/shopGroups';

const Header = ({ onToggleSidebar, onToggleMobileSidebar, onLogout }) => {
  const { selectedShopCode, shopName } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => console.log(err));
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => console.log(err));
        setIsFullscreen(false);
      }
    }
  };

  // Priority: API shop name > config/group shop name > formatted code fallback
  const shopDisplayName = shopName || getShopNameByCode(selectedShopCode);

  return (
    <header className="sticky top-0 z-30 h-16 bg-white/95 dark:bg-[#111827]/95 backdrop-blur-md border-b border-slate-200/90 dark:border-slate-800 px-4 md:px-6 flex items-center justify-between transition-colors duration-200">
      {/* Left side: Hamburger & Branch Tag */}
      <div className="flex items-center gap-3 md:gap-4">
        <button
          onClick={onToggleMobileSidebar}
          className="p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
        >
          <Menu className="w-5 h-5" />
        </button>

        <button
          onClick={onToggleSidebar}
          className="hidden lg:flex p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Toggle Sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Active Shop Badge */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/60">
          <Store className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[200px]" title={shopDisplayName}>
            {shopDisplayName}
          </span>
        </div>
      </div>

      {/* Right side: Actions & User Dropdown */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Dark / Light Mode Switcher */}
        <button
          onClick={toggleTheme}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
        >
          {theme === 'light' ? (
            <Moon className="w-4 h-4" />
          ) : (
            <Sun className="w-4 h-4 text-amber-400" />
          )}
        </button>

        {/* Fullscreen Toggle */}
        <button
          onClick={toggleFullscreen}
          className="hidden sm:flex w-9 h-9 rounded-xl items-center justify-center text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          {isFullscreen ? (
            <Minimize className="w-4 h-4" />
          ) : (
            <Maximize className="w-4 h-4" />
          )}
        </button>

        {/* User Profile Menu */}
        <Dropdown
          align="right"
          trigger={
            <div className="flex items-center gap-2 pl-2 py-1 pr-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shadow-sm shadow-emerald-500/30">
                <User className="w-4 h-4" />
              </div>
              <span className="hidden md:block text-xs font-semibold text-slate-800 dark:text-slate-100">
                Manager
              </span>
            </div>
          }
        >
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700/80">
            <p className="text-xs font-semibold text-slate-900 dark:text-white">Manager</p>
            <p className="text-[11px] text-slate-400 truncate" title={shopDisplayName}>{shopDisplayName}</p>
          </div>
          <div className="py-1">
            <DropdownItem
              onClick={onLogout}
              icon={<LogOut className="w-3.5 h-3.5" />}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30"
            >
              Sign Out
            </DropdownItem>
          </div>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
